import { DebugLog } from "../debug/log";
import type { Idea, ScanProduct, ScanResult } from "../data/types";
import { getActiveEngine } from "./active";
import { SCAN_SYSTEM_PROMPT, buildScanUserMessage, extractFirstJson } from "./prompts";
import type { Settings } from "../data/types";
import type { ModelInfo } from "../data/modelStore";

const TAG = "engine.scan";

export type ScanOutcome =
  | { kind: "success"; result: ScanResult }
  | { kind: "error"; message: string };

export async function runScan(
  idea: Idea,
  settings: Settings,
  installedModel: ModelInfo | null,
): Promise<ScanOutcome> {
  const engine = getActiveEngine(settings, installedModel);
  if (!engine) return { kind: "error", message: "No engine configured." };
  DebugLog.i(TAG, `scan starting for idea ${idea.id}`);
  const result = await engine.complete({
    messages: [
      { role: "system", content: SCAN_SYSTEM_PROMPT },
      { role: "user", content: buildScanUserMessage(idea) },
    ],
    maxTokens: 1024,
    temperature: 0.4,
  });
  if (result.kind === "error") return { kind: "error", message: result.message };
  DebugLog.i(TAG, `scan reply (${result.text.length} chars)`, result.text.slice(0, 300));
  const parsed = extractFirstJson<ScanProduct[]>(result.text);
  if (!parsed || !Array.isArray(parsed) || parsed.length === 0) {
    return {
      kind: "error",
      message: "Engine didn't return a parseable JSON array of products.",
    };
  }
  const products: ScanProduct[] = parsed
    .filter((p) => typeof p === "object" && p !== null)
    .map((p) => ({
      name: String(p.name ?? "").trim(),
      tagline: String(p.tagline ?? "").trim(),
      rationale: String(p.rationale ?? "").trim(),
    }))
    .filter((p) => p.name.length > 0);
  return {
    kind: "success",
    result: {
      scannedAtMs: Date.now(),
      source: "llm-brainstorm",
      searchQueries: [idea.title],
      products,
    },
  };
}
